import { Color, Spacing } from '@persona-id/design-tokens';
import { type Identifier } from 'dnd-core';
import { cloneDeep, isEqual, max } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { DropTargetMonitor, useDrop, XYCoord } from 'react-dnd';
import styled, { css } from 'styled-components';

import {
  DragHandleLocation,
  ItemTypes,
  LAYOUT_COLUMN_TOTAL,
  LAYOUT_ROW_HEIGHT,
  MODULE_ERROR_MESSAGE,
  MODULE_TYPE_TO_NAME,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import {
  getPixelColumnWidth,
  performTopCompaction,
  repositionModule,
  resizeModule,
  resolveCollisions,
} from 'dashboard/apps/DashboardLayoutEditor/gridPositioningUtils';
import {
  LAYOUT_CANVAS_DATA_FOCUS_VALUE,
  UpdateLayout,
} from 'dashboard/apps/DashboardLayoutEditor/LayoutEditor';
import {
  DragBound,
  useGlobalDragEvents,
} from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/helpers/dragHelpers';
import { DraggableGridItem } from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/layers/DraggableGridModule';
import { GridBoxesLayer } from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/layers/GridBoxesLayer';
import { ModuleDragPreviewLayer } from 'dashboard/apps/DashboardLayoutEditor/LayoutPreview/layers/ModuleDragPreviewLayer';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import { BaseModule, ModulePosition } from 'dashboard/lib/module';
import { displayModuleName, generateModuleToken } from 'dashboard/models/case-template';
import Placeholder from 'dashboard/modules/Placeholder';
import Card from 'design-system/components/Card';
import { BASE } from 'design-system/variables/spacing';
import StorageClient, { LocalStorageKey } from 'lib/clients/StorageClient';
import { useBoundingClientRect } from 'lib/hooks/useBoundingClientRect';
import { captureMessage } from 'lib/reporting';

interface DragItem<M extends BaseModule> {
  module: M;
  location?: DragHandleLocation;
}

const GridCard = styled(Card)<{ canvasHeight: number }>`
  background-color: ${Color.CoreSlate100};
  padding: ${Spacing[4]};

  // calculate height with canvas height + padding height
  height: ${({ canvasHeight }) => canvasHeight + BASE * 4}px;
`;

// CanvasWrapper is a convenience element because we don't want padding to factor into position calculations
const CanvasWrapper = styled('div')`
  position: relative;
  height: 100%;
  width: 100%;
`;

export const CanvasLayer = styled('div')`
  position: absolute;
  width: 100%;
`;

export const GridContainer = styled(CanvasLayer)<{ rows: number; disablePointerEvents?: boolean }>`
  display: grid;
  height: 100%;

  ${({ rows, disablePointerEvents }) => css`
    ${disablePointerEvents &&
    'pointer-events: none;'} // disabling pointer events prevents layers from interfering with dragging
    grid-template-columns: repeat(${LAYOUT_COLUMN_TOTAL}, 1fr);
    grid-template-rows: repeat(${rows}, ${LAYOUT_ROW_HEIGHT}px);
  `}
`;

function calculateNextModules<M extends BaseModule>(
  itemType: Identifier | null,
  currentClientOffset: XYCoord | null,
  currentDifferenceFromInitialOffset: XYCoord | null,
  item: DragItem<M>,
  canvasClientBoundingRect: DOMRect | null,
  totalRows: number | undefined,
  currentModules: M[],
  selectedModuleToken: string,
): { nextModules: M[]; nextSelectedModuleToken: string } | null {
  let moduleGridPosition: ModulePosition | null = null;
  // 1. Calculate module position
  switch (itemType) {
    case ItemTypes.LibraryComponent: {
      if (currentClientOffset && canvasClientBoundingRect && totalRows) {
        moduleGridPosition = repositionModule(
          currentClientOffset.x,
          currentClientOffset.y,
          item.module.position,
          canvasClientBoundingRect,
          totalRows,
        );
      }
      break;
    }
    case ItemTypes.Module: {
      if (currentClientOffset && canvasClientBoundingRect && totalRows) {
        moduleGridPosition = repositionModule(
          currentClientOffset.x,
          currentClientOffset.y,
          item.module.position,
          canvasClientBoundingRect,
          totalRows,
        );
      }

      break;
    }
    case ItemTypes.ModuleDragHandle: {
      if (currentDifferenceFromInitialOffset) {
        moduleGridPosition = resizeModule(
          currentDifferenceFromInitialOffset.x,
          currentDifferenceFromInitialOffset.y,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          item.location,
          item.module.position,
          canvasClientBoundingRect,
          totalRows,
        );
      }
      break;
    }
  }

  const nextModules = cloneDeep(currentModules);
  const selectedModuleIndex = nextModules.findIndex((m) => m.token === selectedModuleToken);

  if (moduleGridPosition) {
    const nextModule = {
      ...item.module,
      position: moduleGridPosition,
    };

    // 2. update/add edited module to local modules array
    if (selectedModuleIndex === -1) {
      nextModules.push(nextModule);
    } else {
      nextModules[selectedModuleIndex] = nextModule;
    }

    // 3. Resolve collisions and run layout algorithms
    resolveCollisions(nextModules, item.module.token);
    performTopCompaction(nextModules);

    return { nextModules, nextSelectedModuleToken: nextModule.token };
  } else {
    return null;
  }
}

export interface LayoutCanvasProps<M extends BaseModule> {
  tabToken: string;
  selectedModuleToken: string;
  currentModules: M[];
  renderModule: (module: M) => ReactNode;
  updateLayout: UpdateLayout<M>;
  setSelectedModuleToken: (token: string) => void;
  resetToComponentLibrary: () => void;
}

function LayoutCanvas<M extends BaseModule>({
  tabToken,
  selectedModuleToken: currentSelectedModuleToken,
  currentModules,
  renderModule,
  updateLayout,
  setSelectedModuleToken: setCurrentSelectedModuleToken,
  resetToComponentLibrary,
}: LayoutCanvasProps<M>) {
  const [canvasClientBoundingRect, canvasRef] = useBoundingClientRect<HTMLDivElement>();
  const [modules, setModules] = useState<M[]>(currentModules);

  const [selectedModuleToken, setSelectedModuleToken] = useState(currentSelectedModuleToken);
  const [isDragOverCanvas, setIsDragOverCanvas] = useState(false);

  // these useEffects ensure that top-level state is always the source of truth.
  useEffect(() => {
    setSelectedModuleToken(currentSelectedModuleToken);
  }, [currentSelectedModuleToken]);
  useEffect(() => {
    setModules(currentModules);
  }, [currentModules]);

  // Get the lowest row we need (max of row + height of all modules in this tab)
  // with a minimum of 150 rows.
  const existingRows = max(
    currentModules.map((module: BaseModule) => module.position.y + module.position.height),
  );
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const totalRows = max([150, existingRows + 100]);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const canvasHeight = totalRows * LAYOUT_ROW_HEIGHT;
  const columnWidth = canvasClientBoundingRect?.width
    ? getPixelColumnWidth(canvasClientBoundingRect.width)
    : 0;

  const [, dropRef] = useDrop(
    () => ({
      accept: [ItemTypes.Module, ItemTypes.ModuleDragHandle, ItemTypes.LibraryComponent],
      drop: (item: DragItem<M>, monitor: DropTargetMonitor) => {
        const currentClientOffset = monitor.getSourceClientOffset();
        const currentDifferenceFromInitialOffset = monitor.getDifferenceFromInitialOffset();
        const itemType = monitor.getItemType();

        const nullableNextModules = calculateNextModules(
          itemType,
          currentClientOffset,
          currentDifferenceFromInitialOffset,
          item,
          canvasClientBoundingRect,
          totalRows,
          currentModules,
          selectedModuleToken,
        );

        if (nullableNextModules) {
          const { nextModules, nextSelectedModuleToken } = nullableNextModules;

          updateLayout(tabToken, nextModules);
          /*
           * The dragEnd fires from the ModuleDisplay component in the ComponentLibrary.
           * Unfortunately, because the drop event also causes a module to be selected,
           * the ComponentLibrary ends up unmounting before the dragEnd event is fires,
           * preventing it from firing altogether since the original DOM node has been removed by React.
           * Using setTimeout is a hack to let the event loop to finish before setting selected module token,
           * allowing the event to fire properly before the DOM node unmounts.
           */
          setTimeout(() => {
            setCurrentSelectedModuleToken(nextSelectedModuleToken);
          }, 0);
        }
      },
      hover: (item: DragItem<M>, monitor) => {
        const currentClientOffset = monitor.getSourceClientOffset();
        const currentDifferenceFromInitialOffset = monitor.getDifferenceFromInitialOffset();
        const itemType = monitor.getItemType();

        const nullableNextModules = calculateNextModules(
          itemType as ItemTypes,
          currentClientOffset,
          currentDifferenceFromInitialOffset,
          item,
          canvasClientBoundingRect,
          totalRows,
          currentModules,
          selectedModuleToken,
        );

        if (nullableNextModules) {
          const { nextModules, nextSelectedModuleToken } = nullableNextModules;

          // short circuit if modules are equal so we don't have any unnecessary renders
          if (isEqual(nextModules, modules)) return;

          setModules(nextModules);
          setSelectedModuleToken(nextSelectedModuleToken);
        }
      },
    }),
    [tabToken, modules, canvasClientBoundingRect, currentModules, selectedModuleToken],
  );

  const handleDragEnter = useCallback(() => {
    setIsDragOverCanvas(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOverCanvas(false);

    // this resets our modules when the user drags outside of the canvas
    // the setTimeout is a hack to always have setModules run after all hover events have fired
    // by forcing it to run on the next event loop, otherwise we occasionally see race condition bugs
    setTimeout(() => {
      setModules(currentModules);
    }, 10);
  }, [currentModules]);

  const handleDragEnd = useCallback(() => {
    setIsDragOverCanvas(false);
  }, []);

  useGlobalDragEvents({
    onDragEnd: handleDragEnd,
  });

  const handleModuleSelect = useCallback((token: string) => {
    setCurrentSelectedModuleToken(token);
  }, []);

  useEffect(() => {
    const pasteEventHandler = (e: KeyboardEvent) => {
      // paste is scoped to the layout editor canvas
      // check if the event passed through CanvasWrapper with data-focus attribute
      const path = e.composedPath();
      const sourceDiv = path.find(
        (element) =>
          element instanceof HTMLElement &&
          element.getAttribute('data-focus') === LAYOUT_CANVAS_DATA_FOCUS_VALUE,
      );
      if (!sourceDiv) return;

      if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
        const stringifiedModule = StorageClient.getLocalItem(
          LocalStorageKey.DashboardLayoutEditorCopiedModule,
        );
        if (!stringifiedModule) return;

        const copiedModule = JSON.parse(stringifiedModule) as M;
        // replace current copy module tokens with newly generated
        const newModule = cloneDeep(copiedModule);
        newModule.token = generateModuleToken();

        const nextModules = cloneDeep(currentModules);
        nextModules.push(newModule);
        resolveCollisions(nextModules, newModule.token);
        performTopCompaction(nextModules);
        updateLayout(tabToken, nextModules);
      }
    };

    window.addEventListener('keydown', pasteEventHandler);
    return () => {
      window.removeEventListener('keydown', pasteEventHandler);
    };
  }, [currentModules, tabToken, updateLayout]);

  const gridItems = useMemo(() => {
    return modules.map((module) => {
      return (
        <DraggableGridItem
          module={module}
          key={module.token}
          isSelected={selectedModuleToken === module.token}
          isDragOverCanvas={isDragOverCanvas}
          onModuleSelect={handleModuleSelect}
          columnWidth={columnWidth}
          title={displayModuleName(module)}
        >
          <PersonaErrorBoundary
            onCatch={(error) =>
              captureMessage(`${module.type} failed to load`, 'error', {
                error,
                module: module,
                component: 'DraggableGridItem',
              })
            }
            fallback={() => (
              <Placeholder
                title={`${MODULE_TYPE_TO_NAME[module.type]} module`}
                message={MODULE_ERROR_MESSAGE}
              />
            )}
          >
            {/*
             * RenderModule nests the call to renderModule() which allows us to catch errors with ErrorBoundary.
             * otherwise if we called it inline, the error boundary would not catch errors thrown by renderModule().
             */}
            <RenderModule renderModule={renderModule} module={module} />
          </PersonaErrorBoundary>
        </DraggableGridItem>
      );
    });
  }, [modules, selectedModuleToken, isDragOverCanvas, columnWidth]);

  return (
    <DragBound onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} ref={dropRef}>
      <GridCard canvasHeight={canvasHeight}>
        <CanvasWrapper
          data-test='layout-canvas'
          data-focus={LAYOUT_CANVAS_DATA_FOCUS_VALUE}
          ref={canvasRef}
          // negative value on tab index allows the element to recieve focus without enabling tab navigation
          tabIndex={-1}
          onClick={resetToComponentLibrary}
        >
          {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
          <GridBoxesLayer totalRows={totalRows} />
          {gridItems}
          <ModuleDragPreviewLayer
            isDragOverCanvas={isDragOverCanvas}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            totalRows={totalRows}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            canvasClientBoundingRect={canvasClientBoundingRect}
          />
        </CanvasWrapper>
      </GridCard>
    </DragBound>
  );
}

const RenderModule = ({ renderModule, module }: { renderModule: Function; module: BaseModule }) => {
  return renderModule(module);
};

export default LayoutCanvas;
